import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { api, extractApiError } from '../../lib/api'
import { Breadcrumb } from '../../components/Breadcrumb'
import { calendarTasksResponse } from '../../lib/types'
import { dayKey, getCalendarTimeZone, weekStartWeekday } from '../../lib/format'
import { DateTime } from 'luxon'

// Luxon weekday 1 = Monday … 7 = Sunday.
const WEEKDAY_NAMES = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' }

/** Start of the current month on the company's calendar (the zone dayKey uses). */
const thisMonth = () => DateTime.now().setZone(getCalendarTimeZone() || 'local').startOf('month')

/**
 * /app/calendar — month/week/day view across the user's accessible
 * tasks. Default scope = 'me' (only my assignments); toggle to
 * 'workspace' for everything the caller can see.
 *
 * The grid is built in the person's display zone (their own timezone, else
 * the company's) and starts on their first day of the week, so a cell's day
 * is the same day the task's due date shows everywhere else.
 */
export function CalendarPage() {
  const [anchor, setAnchor] = useState(thisMonth)
  const [scope, setScope] = useState('me')

  const range = useMemo(
    () => ({
      from: anchor.startOf('month').toUTC().toISO(),
      to: anchor.endOf('month').toUTC().toISO(),
    }),
    [anchor],
  )

  const query = useQuery({
    queryKey: ['calendar', range.from, range.to, scope],
    queryFn: async () => {
      const res = await api.get('/calendar/tasks', {
        params: {
          from: range.from,
          to: range.to,
          scope,
        },
      })
      return calendarTasksResponse.parse(res.data)
    },
  })

  const tasksByDay = useMemo(() => {
    const map = new Map()
    for (const t of query.data?.data ?? []) {
      if (!t.dueDate) continue
      // Key by calendar day in the tenant display timezone so a date-only
      // due date lands on the day everyone sees (matches the cell keys).
      const key = dayKey(t.dueDate)
      if (!key) continue
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(t)
    }
    return map
  }, [query.data])

  const firstWeekday = weekStartWeekday()
  // eslint-disable-next-line react-hooks/exhaustive-deps -- buildMonthGrid reads the week start
  const days = useMemo(() => buildMonthGrid(anchor), [anchor, firstWeekday])

  const weekdayHeaders = Array.from(
    { length: 7 },
    (_, i) => WEEKDAY_NAMES[((weekStartWeekday() - 1 + i) % 7) + 1],
  )

  function goPrev() {
    setAnchor((a) => a.minus({ months: 1 }))
  }
  function goNext() {
    setAnchor((a) => a.plus({ months: 1 }))
  }
  function goToday() {
    setAnchor(thisMonth())
  }

  return (
    <div className="h-full flex flex-col">
      <Breadcrumb current="Calendar" />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <div>
          <h1 className="font-display text-2xl text-[color:var(--color-ink-strong)] flex items-center gap-2">
            <CalendarIcon
              size={20}
              className="text-[color:var(--color-accent-strong)]"
            />
            Calendar
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            View your tasks on a calendar.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            className="rounded-[8px] border border-[color:var(--color-rule)] px-2 py-1 text-sm"
          >
            <option value="me">My tasks</option>
            <option value="workspace">All visible tasks</option>
          </select>
          <div className="inline-flex items-center rounded-[8px] border border-[color:var(--color-rule)] overflow-hidden">
            <button
              onClick={goPrev}
              className="px-2 py-1.5 hover:bg-[color:var(--color-card-hover)]"
              aria-label="Previous month"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={goToday}
              className="px-3 py-1.5 text-xs border-l border-r border-[color:var(--color-rule)] hover:bg-[color:var(--color-card-hover)]"
            >
              Today
            </button>
            <button
              onClick={goNext}
              className="px-2 py-1.5 hover:bg-[color:var(--color-card-hover)]"
              aria-label="Next month"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="text-sm font-medium text-[color:var(--color-ink-strong)] min-w-[140px] text-right">
            {/* Calendar header — explicit en-GB locale so the month
             *  name (June vs juin vs giugno) reads consistently for
             *  every user regardless of browser locale settings. The
             *  calendar grid is a date-bucket view, so no time. */}
            {anchor.setLocale('en-GB').toFormat('MMMM yyyy')}
          </div>
        </div>
      </div>

      {query.isLoading ? (
        <div className="text-[color:var(--color-ink-muted)]">Loading…</div>
      ) : query.isError ? (
        <div className="text-red-600 text-sm">
          {extractApiError(query.error).message}
        </div>
      ) : (
        <div className="flex-1 rounded-[10px] border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden">
          <div className="grid grid-cols-7 text-xs font-semibold text-[color:var(--color-ink-muted)] uppercase tracking-wider border-b border-[color:var(--color-rule)]">
            {weekdayHeaders.map((d) => (
              <div key={d} className="px-2 py-2 text-center">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 auto-rows-[minmax(110px,1fr)]">
            {days.map((day, i) => {
              const dayTasks = tasksByDay.get(day.key) ?? []
              return (
                <div
                  key={i}
                  className={`p-1.5 border-r border-b border-[color:var(--color-rule)] ${
                    day.inMonth
                      ? 'bg-[color:var(--color-card)]'
                      : 'bg-[color:var(--color-canvas)]'
                  } ${day.isToday ? 'ring-2 ring-inset ring-[color:var(--color-accent)]/40' : ''}`}
                >
                  <div
                    className={`text-[12px] font-medium mb-1 ${
                      day.inMonth
                        ? 'text-[color:var(--color-ink-strong)]'
                        : 'text-[color:var(--color-ink-muted)]'
                    }`}
                  >
                    {day.date.day}
                  </div>
                  <div className="space-y-0.5 overflow-y-auto max-h-[88px]">
                    {dayTasks.slice(0, 4).map((t) => (
                      <div
                        key={t.id}
                        className="rounded-[4px] px-1.5 py-0.5 text-[12px] truncate"
                        style={{
                          backgroundColor: priorityBg(t.priority),
                          color: priorityFg(t.priority),
                        }}
                        title={t.name}
                      >
                        {t.name}
                      </div>
                    ))}
                    {dayTasks.length > 4 && (
                      <div className="text-[10px] text-[color:var(--color-ink-muted)] pl-1">
                        +{dayTasks.length - 4} more
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

function priorityBg(p) {
  switch (p) {
    case 'urgent':
      return '#fee2e2'
    case 'high':
      return '#fed7aa'
    case 'normal':
      return '#dbeafe'
    case 'low':
      return '#e5e7eb'
    default:
      return '#f3f4f6'
  }
}
function priorityFg(p) {
  switch (p) {
    case 'urgent':
      return '#991b1b'
    case 'high':
      return '#9a3412'
    case 'normal':
      return '#1e40af'
    case 'low':
      return '#374151'
    default:
      return '#374151'
  }
}

/**
 * Builds a 6-row × 7-col grid covering the visible weeks of the anchor month
 * (a Luxon DateTime in the display zone), starting on the person's first day
 * of the week and padded with days of the previous / next month. Each cell's
 * `key` is its calendar day ("yyyy-MM-dd") in the display zone — the same key
 * `dayKey()` gives a task's due date.
 */
function buildMonthGrid(anchor) {
  const first = anchor.startOf('month')
  const start = first.minus({ days: (first.weekday - weekStartWeekday() + 7) % 7 })
  const today = DateTime.now().setZone(anchor.zone).startOf('day')
  return Array.from({ length: 42 }, (_, i) => {
    const d = start.plus({ days: i })
    return {
      date: d,
      key: d.toISODate(),
      inMonth: d.month === first.month,
      isToday: d.hasSame(today, 'day'),
    }
  })
}
